/**
 * Stable App Check debug token for local development, shared across every
 * `localhost` port.
 *
 * THE PROBLEM THIS SOLVES
 * `app.config.ts` used to set `FIREBASE_APPCHECK_DEBUG_TOKEN = true`. With the
 * boolean, the Firebase SDK mints a RANDOM uuid and persists it in web storage
 * — which is scoped per *origin*, and an origin includes the port. So
 * `localhost:4202` got a brand-new token that nobody had registered in
 * Firebase Console → App Check → Manage debug tokens, and every callable
 * answered 401. That is the real reason local work was pinned to `:4201`, and
 * it made two worktrees side by side in the browser impossible (issue #134).
 *
 * THE FIX
 * The SDK also accepts a *string*: when given one it uses that value verbatim
 * and mints nothing. We store the value in a **cookie** rather than in
 * `localStorage`, because cookies are scoped by host and deliberately ignore
 * the port (RFC 6265 §8.5) — one registration, every port. First load on a
 * fresh browser profile generates a uuid, prints it, and asks for it to be
 * registered once; from then on `:4201`, `:4202`, `:4203`… all present the
 * same token.
 *
 * SECURITY
 * A debug token that leaks defeats App Check for the whole Firebase project,
 * so this value must never become public knowledge:
 *   - it lives only in the developer's browser cookie jar — never in the repo,
 *     never in an environment file, never in a built bundle;
 *   - {@link resolveAppCheckDebugToken} returns `null` unless the hostname is
 *     `localhost` / `127.0.0.1`, so a deployed origin (dev.propor.io included)
 *     can neither read nor create one;
 *   - `127.0.0.1` and `localhost` are distinct hosts, so they keep distinct
 *     cookies — use one of the two consistently, or register both tokens.
 *
 * SEEDING AN EXISTING TOKEN
 * A token already registered in the Console can be adopted instead of
 * generating a new one, by loading the app once with
 * `?appCheckDebugToken=<uuid>`. The value is written to the cookie and stripped
 * back out of the URL so it does not linger in browser history.
 */

/** Cookie name. Prefixed to avoid colliding with anything the SDK writes. */
const COOKIE_NAME = 'proporio_appcheck_debug_token';

/** Query parameter used to seed a token that is already registered. */
const SEED_PARAM = 'appCheckDebugToken';

/** One year — long enough that re-registration is not a recurring chore. */
const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/**
 * Firebase debug tokens are uuids. Validating the shape keeps a malformed
 * cookie or a mistyped query parameter from being sent as a token — the SDK
 * would otherwise fail opaquely on every request.
 */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Hostnames on which a debug token is allowed to exist at all. */
const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

/**
 * `true` when the given host is a local dev server. Everything else in this
 * module is gated on it.
 *
 * @param hostname defaults to the current origin's hostname; the parameter
 *   exists so the deployed-origin branch can be covered by a spec (Karma always
 *   serves from `localhost`).
 */
export function isLocalDevOrigin(
  hostname: string | undefined = typeof location !== 'undefined'
    ? location.hostname
    : undefined
): boolean {
  return hostname !== undefined && LOCAL_HOSTNAMES.includes(hostname);
}

/**
 * Resolve the App Check debug token for this browser, or `null` when the
 * current origin is not local (in which case the caller must NOT enable debug
 * mode at all).
 *
 * Order of precedence:
 *   1. a `?appCheckDebugToken=<uuid>` query parameter — adopts an already
 *      registered token and persists it;
 *   2. the existing cookie — the steady state, shared by every localhost port;
 *   3. a freshly generated uuid, persisted and logged for one-time
 *      registration.
 *
 * @param hostname defaults to the current origin's hostname — see
 *   {@link isLocalDevOrigin}.
 */
export function resolveAppCheckDebugToken(
  hostname?: string
): string | null {
  if (!isLocalDevOrigin(hostname) || typeof document === 'undefined') {
    return null;
  }

  const seeded = readSeedParam();
  if (seeded) {
    writeCookie(seeded);
    stripSeedParam();
    logToken(seeded, 'adopted from the URL');
    return seeded;
  }

  const stored = readCookie();
  if (stored) {
    return stored;
  }

  const generated = generateToken();
  writeCookie(generated);
  logToken(generated, 'newly generated');
  return generated;
}

/** Read + validate the seed query parameter. */
function readSeedParam(): string | null {
  const raw = new URLSearchParams(location.search).get(SEED_PARAM);
  return raw && UUID_PATTERN.test(raw) ? raw.toLowerCase() : null;
}

/**
 * Remove the seed parameter from the address bar without reloading, so the
 * token does not sit in browser history or get pasted along with the URL.
 */
function stripSeedParam(): void {
  const url = new URL(location.href);
  url.searchParams.delete(SEED_PARAM);
  history.replaceState(history.state, '', url.toString());
}

/** Read + validate the cookie. Returns `null` when absent or malformed. */
function readCookie(): string | null {
  for (const part of document.cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name === COOKIE_NAME) {
      const value = decodeURIComponent(rest.join('='));
      return UUID_PATTERN.test(value) ? value.toLowerCase() : null;
    }
  }
  return null;
}

/**
 * Persist the token as a host-only cookie. No `Domain` attribute on purpose:
 * a host-only cookie on `localhost` is what makes it port-agnostic without
 * leaking to any other host. `SameSite=Lax` keeps it out of cross-site
 * requests; `Secure` is deliberately omitted because local dev is plain HTTP.
 */
function writeCookie(token: string): void {
  document.cookie =
    `${COOKIE_NAME}=${encodeURIComponent(token)}; path=/; ` +
    `max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
}

/** uuid v4, with a manual fallback for browsers without `randomUUID`. */
function generateToken(): string {
  const cryptoApi =
    typeof crypto !== 'undefined'
      ? (crypto as Crypto & { randomUUID?: () => string })
      : undefined;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  const bytes = new Uint8Array(16);
  cryptoApi?.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
    ''
  );
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20)}`
  );
}

/**
 * Print the token and what to do with it. Kept loud on purpose: an
 * unregistered token turns every callable into a 401, and the console line is
 * the only place the value can be read from.
 */
function logToken(token: string, provenance: string): void {
  console.info(
    `[app-check] debug token (${provenance}): ${token}\n` +
      `[app-check] register it once in Firebase Console → App Check → ` +
      `Manage debug tokens. It is stored in a host-only cookie, so every ` +
      `localhost port reuses it.`
  );
}
